/**
 * Modal for creating or editing a POC reassignment rule configuration.
 * Delegates form state to usePocReassignmentForm and renders section sub-components.
 */

import { Modal, TextInput } from "risa-oasis-ui_v2";
import type { PocReassignmentRuleFormData } from "../../../data-model/pocReassignmentRule";
import type { QueryBuilderConfig } from "../../../data-model/queryBuilder";
import { ModalId } from "../../../enums/modalId";
import ActiveToggleSection from "../shared/ActiveToggleSection";
import { usePaRequestsActionFieldOptions } from "../shared/usePaRequestsActionFieldOptions";
import PocReassignmentActionsSection from "./PocReassignmentActionsSection";
import PocReassignmentTriggersSection from "./PocReassignmentTriggersSection";
import { usePocReassignmentForm } from "./usePocReassignmentForm";

interface AddNewPocReassignmentModalProps {
  /** Condition query builder config (includes top_values per field). */
  conditionConfig: QueryBuilderConfig;
  onCancel: () => void;
  onClose: () => void;
  onSave: (data: PocReassignmentRuleFormData) => void | Promise<void>;
  /** When provided, form is pre-filled and modal is in edit mode. */
  initialData?: PocReassignmentRuleFormData;
}

const AddNewPocReassignmentModal = ({
  conditionConfig,
  onCancel,
  onClose,
  onSave,
  initialData,
}: AddNewPocReassignmentModalProps) => {
  const { fieldOptions, valueConditionConfig } =
    usePaRequestsActionFieldOptions();
  const actionValueConfig =
    valueConditionConfig.fields.length > 0
      ? valueConditionConfig
      : conditionConfig;

  const {
    form,
    formKey,
    isSaving,
    isEditMode,
    setForm,
    updateField,
    handleActionFieldChange,
    handleActionTypeChange,
    handleActionSingleValueChange,
    handleActionMultiValueChange,
    handleAddAction,
    handleRemoveAction,
    handleTriggerTypeChange,
    handleTriggerValueChange,
    handleAddTrigger,
    handleRemoveTrigger,
    handleClose,
    handleCancel,
    handleSave,
  } = usePocReassignmentForm({ initialData, onSave, onClose, onCancel });

  return (
    <Modal
      dialogId={ModalId.ADD_NEW_POC_REASSIGNMENT_MODAL}
      title={
        isEditMode
          ? "Edit POC Reassignment Rule"
          : "Add New POC Reassignment Rule"
      }
      onCancel={handleCancel}
      onClose={handleClose}
      onSave={handleSave}
      saveButtonText={isSaving ? "Saving..." : "Save Configuration"}
      cancelText="Cancel"
      disableSave={isSaving}
    >
      <div
        key={formKey}
        className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto pr-1"
      >
        <TextInput
          id="pocReassignmentRuleLabel"
          label="Rule Label"
          placeholder="e.g. reassign a's orders to d"
          defaultValue={form.label}
          onChange={(data) => updateField("label", data.value ?? "")}
          onBlur={(data) => updateField("label", data.value ?? "")}
        />
        <TextInput
          id="pocReassignmentRuleSubtext"
          label="Subtext (Optional)"
          placeholder="Short description shown on the rule card"
          defaultValue={form.subtext}
          onChange={(data) => updateField("subtext", data.value ?? "")}
          onBlur={(data) => updateField("subtext", data.value ?? "")}
        />

        <PocReassignmentActionsSection
          conditionConfig={actionValueConfig}
          fieldOptions={fieldOptions}
          actions={form.actions}
          onAddAction={handleAddAction}
          onRemoveAction={handleRemoveAction}
          onTypeChange={handleActionTypeChange}
          onFieldChange={handleActionFieldChange}
          onSingleValueChange={handleActionSingleValueChange}
          onMultiValueChange={handleActionMultiValueChange}
        />

        <PocReassignmentTriggersSection
          triggers={form.triggers}
          onAddTrigger={handleAddTrigger}
          onRemoveTrigger={handleRemoveTrigger}
          onTypeChange={handleTriggerTypeChange}
          onValueChange={handleTriggerValueChange}
        />

        <ActiveToggleSection
          id="pocReassignmentRuleActive"
          checked={form.active}
          onToggle={() =>
            setForm((prev) => ({ ...prev, active: !prev.active }))
          }
        />
      </div>
    </Modal>
  );
};

export default AddNewPocReassignmentModal;
